import React, { Component } from 'react'
import PropTypes from 'prop-types'
import { DateRangePicker } from 'react-dates'
import { Map } from 'immutable'
import MomentPropTypes from 'react-moment-proptypes'
import ImmutablePropTypes from 'react-immutable-proptypes'
import cn from 'classnames'
import { values } from 'lodash'
import { withTranslation } from 'react-i18next'

import ErrorLabel from 'app/components/ErrorLabel/ErrorLabel'
import DatePickerArrow from 'app/components/SVGS/DatePickerArrow'
import DatePickerClearArrow from 'app/components/SVGS/DatePickerClearArrow'
import DatePickerInputIcon from 'app/components/SVGS/DatePickerInputIcon'
import { MONTHS } from 'app/constants/months'

import 'react-dates/lib/css/_datepicker.css' // should be before styles.scss
import './styles.scss'

const MIDDLE_WIDTH = 620
const SMALL_WIDTH = 340
const baseClass = 'date-range-field'
/** @see 'react-dates/src/constants' */
const START_DATE = 'startDate'

/**
 * Important!!! in order make this component working you should add `import 'react-dates/initialize'`
 * to your entire file of your project and use this component like a redux fied component:
 *
 * import DateRangeField from '.../DateRangeField'
 *
 * ...
 * <Field
 *   name='dateRange' // or some other name
 *   component={DateRangeField}
 * />
 *
 * @see https://github.com/airbnb/react-dates
 */
export class DateRangeField extends Component {
  static defaultProps = {
    screenWidth: MIDDLE_WIDTH + 1,
    startDatePlaceholderText: 'Start Date',
    endDatePlaceholderText: 'Finish Date',
  }

  static propTypes = {
    input: PropTypes.shape({
      value: ImmutablePropTypes.mapContains({
        startDate: MomentPropTypes.momentObj,
        endDate: MomentPropTypes.momentObj,
      }).isRequired,
      onChange: PropTypes.func.isRequired,
    }).isRequired,
    meta: PropTypes.shape({
      touched: PropTypes.bool,
      error: PropTypes.shape({
        startDate: PropTypes.string,
        endDate: PropTypes.string,
      }),
    }).isRequired,
    screenWidth: PropTypes.number,
    startDatePlaceholderText: PropTypes.string,
    endDatePlaceholderText: PropTypes.string,
  }

  state = {
    focusedInput: null,
  }

  handleCorrectInput = e => {
    const keyButton = e.keyCode
    const value = e.target.value
    const [day, month, year] = value.split('-')

    if (/[^\d|-]/gi.test(day)) {
      e.target.value = day.replace(/[^\d|-]/gi, '')
      return
    }

    if (year && /[^\d|-]/gi.test(year)) {
      e.target.value = `${day}-${month}-${year.replace(/[^\d|-]/gi, '')}`
      return
    }
    const isOneDigitInDay = /^\d\//.test(value)
    const isYearExist = /\d{2}$/.test(value)

    switch (keyButton) {
      case 8: // Backspace
      case 37: // left
      case 38: // up
      case 39: // right
      case 40: // down
        return
      default:
        // autoaddition 0 before digit
        if (isOneDigitInDay && !isYearExist) e.target.value = value.replace(/\/\d\//, `/0${value[0]}-/`)

        // autoaddition slash to the end string
        if (!isOneDigitInDay && value.length === 2) e.target.value = `${value}-`

        if (value.length === 5 && !isNaN(parseInt(month))) {
          let newValue = MONTHS[month]
          if (!newValue) {
            e.target.value = `${day}-${MONTHS['01']}-`
          } else {
            e.target.value = `${day}-${newValue}-`
          }
        }

        if (month && value.length === 6 && isNaN(parseInt(month))) {
          let formatedMonth = month[0].toUpperCase() + month.substring(1)
          ~values(MONTHS).indexOf(formatedMonth)
            ? (e.target.value = `${day}-${formatedMonth}-`)
            : (e.target.value = `${day}-${MONTHS['01']}-`)
        }
        this.handleValidationInput(e)
    }
  }

  handleValidationInput = e => {
    const name = ~e.target.id.indexOf('start') ? 'Start Date' : 'Finish Date'
    const value = e.target.value
    switch (true) {
      case !/\d{2}-\w{3}-\d{4}/.test(value):
        this.props.meta.touched = true
        this.props.meta.error = { startDate: `${name} Format must be DD-MMM-YY` }
        break
      case parseInt(value.substr(0, 2)) > 31:
        this.props.meta.touched = true
        this.props.meta.error = { startDate: `${name} Please enter a valid date` }
        break
      case !MONTHS.find(month => month === value.substr(3, 3)):
        this.props.meta.touched = true
        this.props.meta.error = { startDate: `${name} Please enter a valid month` }
        break

      default:
        this.props.meta.touched = false
    }
  }

  componentDidMount() {
    const inputStartDate = document.getElementById(`start-date-${this.props.input.name}`)
    inputStartDate.maxLength = 9
    inputStartDate.addEventListener('keyup', this.handleCorrectInput)
    const inputEndDate = document.getElementById(`end-date-${this.props.input.name}`)
    inputEndDate.maxLength = 9
    inputEndDate.addEventListener('keyup', this.handleCorrectInput)
    // add autofocus if first initialization
    if (!this.props.input.value.get('startDate')) {
      this.setState({ focusedInput: START_DATE })
    }
  }

  componentWillUnmount() {
    const inputStartDate = document.getElementById(`start-date-${this.props.input.name}`)
    inputStartDate.removeEventListener('keyup', this.handleCorrectInput)
    const inputEndDate = document.getElementById(`end-date-${this.props.input.name}`)
    inputEndDate.removeEventListener('keyup', this.handleCorrectInput)
  }

  handleDatesChange = value => {
    // TODO: remove the name if not exists
    return this.props.input.onChange(
      Map({
        name: this.props.input.value.get('name'),
        ...value,
      })
    )
  }

  handleFocusChange = focusedInput => {
    return this.setState({ focusedInput })
  }

  render() {
    const {
      t,
      input: { value, name },
      meta: { touched, error },
      className = '',
      screenWidth,
      startDatePlaceholderText,
      endDatePlaceholderText,
    } = this.props
    const { focusedInput } = this.state
    return (
      <div
        className={cn(baseClass, className, {
          [`${baseClass}__error-start`]: touched && error && error.startDate,
          [`${baseClass}__error-end`]: touched && error && error.endDate,
        })}
      >
        <DateRangePicker
          startDateId={`start-date-${name}`}
          endDateId={`end-date-${name}`}
          startDate={value.get('startDate')} // momentPropTypes.momentObj or null,
          endDate={value.get('endDate')} // momentPropTypes.momentObj or null,
          onDatesChange={this.handleDatesChange} // PropTypes.func.isRequired,
          focusedInput={focusedInput} // PropTypes.oneOf([START_DATE, END_DATE]) or null,
          onFocusChange={this.handleFocusChange} // PropTypes.func.isRequired,
          numberOfMonths={screenWidth > MIDDLE_WIDTH ? 2 : 1}
          startDatePlaceholderText={t(startDatePlaceholderText)}
          endDatePlaceholderText={t(endDatePlaceholderText)}
          withFullScreenPortal={screenWidth < SMALL_WIDTH}
          orientation={screenWidth < SMALL_WIDTH ? 'vertical' : 'horizontal'}
          noBorder
          showClearDates
          minimumNights={0} // if 0 it means that allowed single day range
          displayFormat="DD-MMM-YY"
          hideKeyboardShortcutsPanel
          customArrowIcon={<DatePickerArrow />}
          customCloseIcon={<DatePickerClearArrow />}
          customInputIcon={<DatePickerInputIcon width="14" height="14" />}
          showDefaultInputIcon
        />
        {touched &&
          error &&
          (error.startDate || error.endDate) && <ErrorLabel error={t(`common:${error.startDate || error.endDate}`)} />}
      </div>
    )
  }
}

export default withTranslation(['user', 'common'])(DateRangeField)
